import { openDB } from "idb";

const DB_NAME = "marinebytesDB";
const DB_VERSION = 1;
const STORE_NAME = "engine_log_book";

// Initialize database
const initDB = async () => {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, {
          keyPath: "id",
          autoIncrement: true,
        });
        store.createIndex("timestamp", "timestamp", { unique: false });
      }
    },
  });
};

export const addEntry = async (entry) => {
  const timestamp = new Date().toISOString();
  const db = await initDB();
  const tx = db.transaction(STORE_NAME, "readwrite");

  const entryWithTimestamps = {
    ...entry,
    created_at: timestamp,
    updated_at: timestamp,
  };
  await tx.store.add(entryWithTimestamps);
  await tx.done;
  console.log("✅ Entry added:", entryWithTimestamps);
};

// Get all entries
export const getAllEntries = async () => {
  const db = await initDB();
  const data = await db.getAll(STORE_NAME);
  console.log("📦 All entries fetched:", data);
  return data;
};

export const getEntryByVoyageId = async (voyage_id) => {
  try {
    const db = await initDB();
    const entry = await db.get(STORE_NAME, voyage_id);
    console.log("📦 All entries fetched:", entry?.data);
    return entry?.data || [];
  } catch (error) {
    console.error("❌ Failed to fetch entry by voyage_id:", error);
    return [];
  }
};

// Get entry by ID
export const getEntryById = async (id) => {
  const db = await initDB();
  const entry = await db.get(STORE_NAME, id);
  console.log(`📌 Entry with ID ${id}:`, entry);
  return entry;
};

// Update entry by ID
export const updateEntry = async (id, updatedData) => {
  const db = await initDB();
  const existing = await db.get(STORE_NAME, id);
  if (!existing) throw new Error("Entry not found");

  const updatedEntry = { ...existing, ...updatedData, id };
  const tx = db.transaction(STORE_NAME, "readwrite");
  await tx.store.put(updatedEntry);
  await tx.done;
  console.log("🔁 Entry updated:", updatedEntry);
};

// Delete entry by ID
export const deleteEntry = async (id) => {
  const db = await initDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  await tx.store.delete(id);
  await tx.done;
  console.log(`🗑️ Entry with ID ${id} deleted`);
};

export const deleteEntryById = async (id) => {
  try {
    const db = await initDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    await tx.store.delete(id);
    await tx.done;
    console.log(`🗑️ Entry with ID ${id} deleted`);
  } catch (error) {
    console.error("❌ Failed to delete entry:", error);
  }
};

// Count total entries
export const countEntries = async () => {
  const db = await initDB();
  const count = await db.count(STORE_NAME);
  console.log("🔢 Total entries:", count);
  return count;
};

// Clear all entries in store
export const clearAllEntries = async () => {
  const db = await initDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  await tx.store.clear();
  await tx.done;
  console.log("🚮 All entries cleared from store");
};

// Delete entire database
export const deleteDatabase = async () => {
  return new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = () => {
      console.log("💥 Database deleted successfully");
      resolve();
    };
    req.onerror = (e) => {
      console.error("❌ Error deleting database:", e);
      reject(e);
    };
    req.onblocked = () => {
      console.warn("⚠️ Delete blocked — close other tabs using this DB.");
    };
  });
};

// Optional: Auto-clear on first page load (session only)
export const autoClearOncePerSession = async () => {
  if (!sessionStorage.getItem("clearedIndexedDB")) {
    await clearAllEntries();
    sessionStorage.setItem("clearedIndexedDB", "true");
  }
};
